import { useCogniteClient } from '#core/hooks/useCogniteClient.ts';
import {
  columnsFromView,
  type DataModelView,
  KG_QUERY_MAX_LIMIT,
  type MintSeriesSource,
  materializeInstanceRows,
  viewToSchema,
} from '#core/lib/cognite/view.ts';
import { LimitTooLargeError, UnknownDatasetError } from '#core/lib/expr/errors.ts';
import type { AstInput } from '#core/lib/expr/normalize.ts';
import { isPushableNode } from '#core/lib/expr/ops/knowledge-graph/pushdown.ts';
import { buildCdfViewQuery, CDF_VIEW_PROVIDER_ID, type CdfViewNative } from '#core/lib/expr/providers/cdf-view.ts';
import { KNOWLEDGE_GRAPH_GROUP } from '#core/lib/expr/providers/groups.ts';
import { type PipelineHooks, parseSourcePipeline } from '#core/lib/expr/providers/pipeline.ts';
import type { DatasetProvider, DecodedParams, PlanCtx, ProviderSource } from '#core/lib/expr/providers/types.ts';
import { projectSchema } from '#core/lib/expr/schema/types.ts';
import type { MaterializedDataset } from '#core/lib/expr/value.ts';

/**
 * The CDF pipeline hooks driving the shared plan-time fold. `isPushable`
 * binds the resolved view and delegates to the real CDF recognizer;
 * `translate` is the IDENTITY so the absorbed `native` filter stays raw
 * AST (the DMS translation happens at fetch time, where the view is in
 * scope). `absorbableConstraints` lists the non-predicate constraints CDF
 * applies natively: the `columns` wire projection and the `limit` node
 * cap. CDF does NOT absorb `sort` — it cannot guarantee deterministic
 * multi-instance ordering, so sort always re-emits as a residual op. The
 * ordering-legality planner still gates each absorption (e.g. `limit` is
 * kept residual when a residual filter precedes it, avoiding an
 * under-count).
 */
const cdfHooks = (view: DataModelView): PipelineHooks<AstInput> => ({
  isPushable: (node, schema) => isPushableNode(node, schema, view),
  translate: (pushed) => pushed,
  absorbableConstraints: ['columns', 'limit'],
});

export interface UseCdfProviderOptions {
  readonly views: readonly DataModelView[];
  readonly mintSeries?: MintSeriesSource;
  readonly id?: string;
}

/**
 * Build the CDF view provider over the available data-model views,
 * closing over a `CogniteClient` for the instance-query executor. `id`
 * is the dispatch + cache discriminator (`ProviderSource.providerId`); the
 * views are captured by reference — the host (`ChatSessionProvider`)
 * owns their lifetime and builds the provider once per `(client, views)`.
 * `mintSeries` is the timeseries provider's `buildSource`, used by `fetch`
 * to mint each per-row timeseries cell so a view's nested datapoints cell
 * is constructed identically to a top-level `(datapoints)` row cell.
 *
 * The provider is a pure LEAF: it implements `claims` / `schema` / `plan`
 * / `collapseRef` / `fetch`. It deliberately has no `rewrite` (it is not a
 * facade) and no `buildSource` of its own — its `plan` mints the view
 * source directly so the pushed filter, columns, and limit fold into one
 * `native` blob in a single pass.
 */
export const useCdfViewProvider = ({
  id = CDF_VIEW_PROVIDER_ID,
  views,
  mintSeries,
}: UseCdfProviderOptions): DatasetProvider<DecodedParams, CdfViewNative> => {
  const viewsByExternalId = new Map(views.map((v) => [v.externalId, v]));
  const viewFor = (name: string): DataModelView | undefined => viewsByExternalId.get(name);
  const client = useCogniteClient();
  if (!mintSeries) {
    throw new Error('mintSeries required to mint per-row view timeseries cells');
  }

  return {
    id,
    group: KNOWLEDGE_GRAPH_GROUP,
    claims: (name) => viewFor(name) !== undefined,
    schema: (name) => {
      const view = viewFor(name);
      if (view === undefined) {
        throw new Error(`CDF view provider '${id}' was asked for the schema of an unclaimed view '${name}'`);
      }
      return viewToSchema(view);
    },

    // Agent-facing docs for the DescribeViews tool: the view's own
    // description plus its typed columns (Zod introspection, no data scan).
    describe: (name) => {
      const view = viewFor(name);
      if (view === undefined) {
        return undefined;
      }
      return {
        description: (view.schema.out as { description?: string }).description,
        columns: columnsFromView(view),
      };
    },

    plan: (name, params: DecodedParams, ctx: PlanCtx) => {
      const view = viewFor(name);
      if (view === undefined) {
        // Resolution already happened in the source op, but guard the
        // direct-call path so a wiring mistake fails loudly.
        throw new UnknownDatasetError(name, [...viewsByExternalId.keys()], ctx.pos);
      }
      if (params.limit !== undefined && params.limit > KG_QUERY_MAX_LIMIT) {
        throw new LimitTooLargeError(params.limit, KG_QUERY_MAX_LIMIT, ctx.pos);
      }

      const schema = viewToSchema(view);
      // Run the shared plan-time fold over the literalised constraints with
      // the CDF hooks: it partitions the predicate (the pushable part rides
      // `native` as raw AST via the identity `translate`), gates the
      // non-predicate constraints through the ordering-legality rule, and
      // assembles whatever was not absorbed into ordered residual ops. One
      // SSOT for the partition + legality + projection — CDF only supplies
      // pushability + which constraints it can apply natively.
      const parsed = parseSourcePipeline(params, schema, cdfHooks(view));

      // A non-predicate constraint is absorbed into `native` iff it is
      // present AND the legality planner did NOT leave it residual. CDF
      // applies an absorbed `columns` as the wire-level projection and an
      // absorbed `limit` as the instance-query node cap; a constraint kept
      // residual replays client-side instead and is therefore omitted here.
      const residualKinds = new Set(parsed.residualOps.map((op) => op.op));
      const absorbedColumns = params.columns !== undefined && !residualKinds.has('columns');
      const absorbedLimit = params.limit !== undefined && !residualKinds.has('limit');

      const native: CdfViewNative = {
        view: name,
        ...(parsed.native === undefined ? {} : { filter: parsed.native }),
        ...(absorbedColumns ? { columns: params.columns } : {}),
        ...(absorbedLimit ? { limit: params.limit } : {}),
      };
      const source: ProviderSource = { kind: 'provider', providerId: id, native };
      return {
        source,
        residual: parsed.residualOps,
        // The fold's post-projection schema — declared == fetched, so the
        // size-strict `schemaRefines` post-condition holds after fetch +
        // residual replay (whether the projection rode native or residual).
        schema: parsed.schema,
      };
    },

    // Non-relation dataset operand: collapse to the RHS `externalId` column
    // (a space-agnostic externalId match). A RELATION-membership LHS does NOT
    // reach here — it resolves directly from the source dataset's typed
    // identity axis so the target {space, externalId} is taken from the
    // source's real node identity, never assumed. The nested-sub-query pre-pass
    // applies this before partition; a non-CDF provider falls back to the
    // generic single-column rule.
    collapseRef: (_lhsCol, rhs): AstInput => [...new Set(rhs.columns.externalId ?? [])] as readonly AstInput[],

    fetch: async (source, ctx): Promise<MaterializedDataset> => {
      const native = source.native as CdfViewNative | undefined;
      const view = native === undefined ? undefined : viewFor(native.view);
      if (native === undefined || view === undefined) {
        throw new Error(
          `CDF view provider '${id}' cannot fetch source for view '${native?.view ?? '(missing)'}' — not registered`,
        );
      }
      // Resolve any interned identity handle in the pushed filter back to its
      // real ref vector via the SAME session that interned it (the handle only
      // rode `native` to keep the source cache key small).
      const payload = buildCdfViewQuery(native, view, ctx.session.resolveInterned);
      const rows = await view.query(client, payload);
      // Project to the requested columns with the SAME projection the
      // declared schema used, so the materialised (actual) schema refines
      // the declared one — `viewToSchema` always prepends space/externalId,
      // so a `:columns` subset would otherwise fail size-strict refinement.
      const schema = projectSchema(viewToSchema(view), native.columns);
      // Materialise over the projected schema, populating the typed identity
      // axis from the SAME node rows the columns are projected from, so the
      // axis and the denormalised space/externalId columns never drift.
      return materializeInstanceRows(rows, schema, view, mintSeries);
    },
  };
};
